import React, { useState,useEffect } from "react";
import { useNavigate,useParams, } from "react-router-dom";
import {
  Briefcase,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  BookOpen,
  Link,
  Mail,
  Plus,
  X,
  Save,
  AlertCircle,
  Globe,
  Info,
  ChevronRight,
  Code,
  Target,
  Hash,
  Loader2
} from "lucide-react";

const CreateJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [error, setError] = useState("");
  const [jobPost, setJobPost] = useState({
    jobId: "", 
    company: {
      name: "",
      website: "",
      location: "",
    },
    jobDetails: {
      title: "",
      description: "",
      type: "",
      experience: {
        min: "",
        max: "",
      },
      salary: {
        min: "",
        max: "",
        currency: "INR",
        isNegotiable: false,
      },
      skills: [],
      qualifications: [],
    },
    applicationDetails: {
      deadline: "",
      applicationUrl: "",
      applicationEmail: "",
    },
    referralEnabled: false,
    referralDetails: {
      enabled: false,
      instructions: "",
      rewards: "",
    },
  });

  useEffect(() => {
    if (id) {
      fetchJobDetails();
    }
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`http://localhost:50001/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch job details");
      }

      const data = await response.json();
      
      // Transform the data to match the form structure
      setJobPost({
        company: {
          name: data.company.name || "",
          website: data.company.website || "",
          location: data.company.location || "",
        },
        jobDetails: {
          title: data.jobDetails.title || "",
          description: data.jobDetails.description || "",
          type: data.jobDetails.type || "",
          experience: {
            min: data.jobDetails.experience?.min || "",
            max: data.jobDetails.experience?.max || "",
          },
          salary: {
            min: data.jobDetails.salary?.min || "",
            max: data.jobDetails.salary?.max || "",
            currency: data.jobDetails.salary?.currency || "INR",
            isNegotiable: data.jobDetails.salary?.isNegotiable || false,
          },
          skills: data.jobDetails.skills || [],
          qualifications: data.jobDetails.qualifications || [],
        },
        applicationDetails: {
          deadline: data.applicationDetails?.deadline 
            ? new Date(data.applicationDetails.deadline).toISOString().split('T')[0]
            : "",
          applicationUrl: data.applicationDetails?.applicationUrl || "",
          applicationEmail: data.applicationDetails?.applicationEmail || "",
        },
        referralEnabled: data.referralEnabled || false,
        referralDetails: {
          enabled: data.referralDetails?.enabled || false,
          instructions: data.referralDetails?.instructions || "",
          rewards: data.referralDetails?.rewards || "",
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const url = id 
        ? `http://localhost:50001/api/jobs/${id}`
        : "http://localhost:50001/api/jobs";

      const response = await fetch(url, {
        method: id ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: jobPost.jobId,
          company: {
            name: jobPost.company.name,
            location: jobPost.company.location,
            website: jobPost.company.website,
          },
          jobDetails: {
            title: jobPost.jobDetails.title,
            description: jobPost.jobDetails.description,
            type: jobPost.jobDetails.type,
            experience: {
              min: parseInt(jobPost.jobDetails.experience.min) || 0,
              max: parseInt(jobPost.jobDetails.experience.max) || 0,
            },
            salary: {
              min: parseInt(jobPost.jobDetails.salary.min) || 0,
              max: parseInt(jobPost.jobDetails.salary.max) || 0,
              isNegotiable: jobPost.jobDetails.salary.isNegotiable,
              currency: jobPost.jobDetails.salary.currency,
            },
            skills: jobPost.jobDetails.skills,
            qualifications: jobPost.jobDetails.qualifications,
          },
          applicationDetails: {
            deadline: jobPost.applicationDetails.deadline,
            applicationEmail: jobPost.applicationDetails.applicationEmail,
            applicationUrl: jobPost.applicationDetails.applicationUrl,
          },
          referralEnabled: jobPost.referralEnabled,
          referralDetails: jobPost.referralEnabled ? {
            enabled: true,
            instructions: jobPost.referralDetails.instructions,
            rewards: jobPost.referralDetails.rewards,
          } : {
            enabled: false,
          },
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `Failed to ${id ? 'update' : 'create'} job post`);
      }

      // Show success message
      alert(`Job post ${id ? 'updated' : 'created'} successfully!`);
      navigate("/jobs");
    } catch (err) {
      console.error(`Error ${id ? 'updating' : 'creating'} job:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const formSteps = [
    {
      id: "basics",
      title: "Basic Info",
      icon: Building,
      description: "Start with company details",
    },
    {
      id: "details",
      title: "Job Details",
      icon: Briefcase,
      description: "Add role specifics",
    },
    {
      id: "application",
      title: "Application",
      icon: Target,
      description: "Set application process",
    },
  ];
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col items-center text-center">
            <div className="p-3 bg-blue-50 rounded-xl mb-6">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {id ? 'Edit Job Post' : 'Post a New Job'}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl">
              {id ? 'Update job details and requirements' : 'Share career opportunities with your alumni network'}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="relative -mt-8 mb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between">
              {formSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => setCurrentStep(index)}
                >
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full mb-2
                    transition-all duration-200 ${
                      index <= currentStep
                        ? "bg-blue-50 border-2 border-blue-500"
                        : "bg-gray-100 border-2 border-transparent"
                    }`}
                  >
                    <step.icon
                      className={`h-5 w-5 ${
                        index <= currentStep ? "text-blue-600" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <div className="text-center">
                    <p className={`text-sm font-medium ${
                      index <= currentStep ? "text-gray-900" : "text-gray-500"
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500 hidden md:block">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className={`${currentStep === 0 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Company Details
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Tell us about the company offering this position
                    </p>
                  </div>
                  <Building className="h-5 w-5 text-gray-400" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={jobPost.company.name}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            company: { ...jobPost.company, name: e.target.value },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Google, Microsoft"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={jobPost.company.location}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            company: { ...jobPost.company, location: e.target.value },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., New York, Remote"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Website
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="url"
                        value={jobPost.company.website}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            company: { ...jobPost.company, website: e.target.value },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="https://company-website.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Job Information
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Basic details about the position
                    </p>
                  </div>
                  <Briefcase className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Job ID 
  </label>
  <div className="relative">
    <Hash className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
    <input
      type="text"
      value={jobPost.jobId}
      required
      onChange={(e) =>
        setJobPost({
          ...jobPost,
          jobId: e.target.value,
        })
      }
      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
      placeholder="Enter Job ID"
    />
  </div>
</div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={jobPost.jobDetails.title}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            jobDetails: {
                              ...jobPost.jobDetails,
                              title: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Senior Software Engineer"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Type
                      </label>
                      <select
                        required
                        value={jobPost.jobDetails.type}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            jobDetails: {
                              ...jobPost.jobDetails,
                              type: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Type</option>
                        <option value="full-time">Full Time</option>
                        <option value="part-time">Part Time</option>
                        <option value="part-time">Remote</option>
                        <option value="contract">Contract</option>
                        <option value="internship">Internship</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Application Deadline
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <input
                          type="date"
                          required
                          value={jobPost.applicationDetails.deadline}
                          onChange={(e) =>
                            setJobPost({
                              ...jobPost,
                              applicationDetails: {
                                ...jobPost.applicationDetails,
                                deadline: e.target.value,
                              },
                            })
                          }
                          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Description
                    </label>
                    <textarea
                      required
                      value={jobPost.jobDetails.description}
                      onChange={(e) =>
                        setJobPost({
                          ...jobPost,
                          jobDetails: { ...jobPost.jobDetails,
                            description: e.target.value,
                          },
                        })
                      }
                      rows={6}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe the role, responsibilities, and requirements..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Job Details Section */}
          <div className={`${currentStep === 1 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Experience & Compensation
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Define experience requirements and compensation details
                    </p>
                  </div>
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Experience Range (Years)
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="number"
                          value={jobPost.jobDetails.experience.min}
                          onChange={(e) =>
                            setJobPost({
                              ...jobPost,
                              jobDetails: {
                                ...jobPost.jobDetails,
                                experience: {
                                  ...jobPost.jobDetails.experience,
                                  min: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          value={jobPost.jobDetails.experience.max}
                          onChange={(e) =>
                            setJobPost({
                              ...jobPost,
                              jobDetails: {
                                ...jobPost.jobDetails,
                                experience: {
                                  ...jobPost.jobDetails.experience,
                                  max: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Max"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Salary Range
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <input
                            type="number"
                            value={jobPost.jobDetails.salary.min}
                            onChange={(e) =>
                              setJobPost({
                                ...jobPost,
                                jobDetails: {
                                  ...jobPost.jobDetails,
                                  salary: {
                                    ...jobPost.jobDetails.salary,
                                    min: e.target.value,
                                  },
                                },
                              })
                            }
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                            placeholder="Min"
                          />
                        </div>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <input
                            type="number"
                            value={jobPost.jobDetails.salary.max}
                            onChange={(e) =>
                              setJobPost({
                                ...jobPost,
                                jobDetails: {
                                  ...jobPost.jobDetails,
                                  salary: {
                                    ...jobPost.jobDetails.salary,
                                    max: e.target.value,
                                  },
                                },
                              })
                            }
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                            placeholder="Max"
                          />
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={jobPost.jobDetails.salary.isNegotiable}
                            onChange={(e) =>
                              setJobPost({
                                ...jobPost,
                                jobDetails: {
                                  ...jobPost.jobDetails,
                                  salary: {
                                    ...jobPost.jobDetails.salary,
                                    isNegotiable: e.target.checked,
                                  },
                                },
                              })
                            }
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                          />
                          <span className="ml-2 text-sm text-gray-600">
                            Salary is negotiable
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Required Skills & Qualifications
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      List the skills and qualifications needed for this role
                    </p>
                  </div>
                  <Code className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Skills
  </label>
  <div className="space-y-3">
    {/* Dropdown for Common Skills */}
    <div className="flex gap-2">
      <select
        className="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
        onChange={(e) => {
          const selectedSkill = e.target.value;
          if (selectedSkill && !jobPost.jobDetails.skills.includes(selectedSkill)) {
            setJobPost({
              ...jobPost,
              jobDetails: {
                ...jobPost.jobDetails,
                skills: [...jobPost.jobDetails.skills, selectedSkill],
              },
            });
          }
          e.target.value = ""; // Reset dropdown selection
        }}
      >
        <option value="" disabled selected>
          Select a skill
        </option>
        {[
  "JavaScript", "Python", "React", "Node.js", "Java", "CSS", "SQL", 
  "HTML", "TypeScript", "C#", "C++", "Ruby", "PHP", "Go", "Kotlin", 
  "Swift", "Angular", "Vue.js", "Docker", "Kubernetes", "AWS", 
  "Azure", "Google Cloud", "Machine Learning", "Data Analysis", 
  "Data Visualization", "DevOps", "UI/UX Design", "Agile Methodology", 
  "Scrum", "Project Management", "Version Control (Git)", "REST APIs", 
  "GraphQL", "Mobile Development", "Responsive Design", 
  "Test Automation", "Software Testing", "Cybersecurity", 
  "Blockchain Development", "Artificial Intelligence"
].map(skill => (
  <option key={skill} value={skill}>
    {skill}
  </option>
))}
      </select>
    </div>

    {/* Comma-separated input field */}
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Code className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={jobPost.jobDetails.skills.join(", ")}
          onChange={(e) =>
            setJobPost({
              ...jobPost,
              jobDetails: {
                ...jobPost.jobDetails,
                skills: e.target.value.split(",").map((s) => s.trim()),
              },
            })
          }
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
          placeholder="Enter skills separated by commas"
        />
      </div>
    </div>

    {/* Display skills as tags */}
    {jobPost.jobDetails.skills.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {jobPost.jobDetails.skills.map((skill, index) => (
          <span
            key={index}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700"
          >
            {skill}
            <button
              type="button"
              onClick={() => {
                const newSkills = jobPost.jobDetails.skills.filter((_, i) => i !== index);
                setJobPost({
                  ...jobPost,
                  jobDetails: {
                    ...jobPost.jobDetails,
                    skills: newSkills,
                  },
                });
              }}
              className="ml-2 inline-flex items-center p-0.5 rounded-full text-blue-400 hover:bg-blue-100"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
</div>


<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Qualifications
  </label>
  <div className="space-y-3">
    {/* Dropdown for Common Qualifications */}
    <div className="flex gap-2">
      <select
        className="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
        onChange={(e) => {
          const selectedQualification = e.target.value;
          if (
            selectedQualification &&
            !jobPost.jobDetails.qualifications.includes(selectedQualification)
          ) {
            setJobPost({
              ...jobPost,
              jobDetails: {
                ...jobPost.jobDetails,
                qualifications: [
                  ...jobPost.jobDetails.qualifications,
                  selectedQualification,
                ],
              },
            });
          }
          e.target.value = ""; // Reset dropdown selection
        }}
      >
        <option value="" disabled selected>
          Select a qualification
        </option>
        {[
          "Bachelor's in Computer Science",
          "Bachelor's in Business Administration",
          "Master's in Computer Science",
          "Master's in Business Administration",
          "PhD in Artificial Intelligence",
          "Associate's Degree in Information Technology",
        ].map((qualification) => (
          <option key={qualification} value={qualification}>
            {qualification}
          </option>
        ))}
      </select>
    </div>

    {/* Comma-separated input field */}
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Code className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={jobPost.jobDetails.qualifications.join(", ")}
          onChange={(e) =>
            setJobPost({
              ...jobPost,
              jobDetails: {
                ...jobPost.jobDetails,
                qualifications: e.target.value.split(",").map((q) => q.trim()),
              },
            })
          }
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
          placeholder="Enter qualifications separated by commas"
        />
      </div>
    </div>

    {/* Display qualifications as tags */}
    {jobPost.jobDetails.qualifications.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {jobPost.jobDetails.qualifications.map((qualification, index) => (
          <span
            key={index}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700"
          >
            {qualification}
            <button
              type="button"
              onClick={() => {
                const newQualifications = jobPost.jobDetails.qualifications.filter(
                  (_, i) => i !== index
                );
                setJobPost({
                  ...jobPost,
                  jobDetails: {
                    ...jobPost.jobDetails,
                    qualifications: newQualifications,
                  },
                });
              }}
              className="ml-2 inline-flex items-center p-0.5 rounded-full text-green-400 hover:bg-green-100"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
</div>

                </div>
              </div>
            </div>
          </div>

          {/* Application Section */}
          <div className={`${currentStep === 2 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Application Process
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Set up how candidates can apply for this position
                    </p>
                  </div>
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Application Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={jobPost.applicationDetails.applicationEmail}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            applicationDetails: {
                              ...jobPost.applicationDetails,
                              applicationEmail: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="hr@company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Application URL 
                    </label>
                    <div className="relative">
                      <Link className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="url"
                        required
                        value={jobPost.applicationDetails.applicationUrl}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            applicationDetails: {
                              ...jobPost.applicationDetails,
                              applicationUrl: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="https://careers.company.com/apply"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Referral Program
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Enable and configure referral options
                    </p>
                  </div>
                  <Target className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={jobPost.referralEnabled}
                        onChange={(e) =>
                          setJobPost({
                            ...jobPost,
                            referralEnabled: e.target.checked,
                            referralDetails: {
                              ...jobPost.referralDetails,
                              enabled: e.target.checked,
                            },
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Enable Referral Program
                        </span>
                        <span className="block text-sm text-gray-500">
                          Allow alumni to refer candidates for this position
                        </span>
                      </span>
                    </label>
                  </div>

                  {jobPost.referralEnabled && (
                    <div className="pt-6 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Referral Instructions
                        </label>
                        <textarea
                          value={jobPost.referralDetails.instructions}
                          onChange={(e) =>
                            setJobPost({
                              ...jobPost,
                              referralDetails: {
                                ...jobPost.referralDetails,
                                instructions: e.target.value,
                              },
                            })
                          }
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Provide instructions for the referral process..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Referral Rewards
                        </label>
                        <textarea
                          value={jobPost.referralDetails.rewards}
                          onChange={(e) =>
                            setJobPost({
                              ...jobPost,
                              referralDetails: {
                                ...jobPost.referralDetails,
                                rewards: e.target.value,
                              },
                            })
                          }
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe any rewards for successful referrals..."
                        />
                      </div>

                      <div className="bg-blue-50 rounded-xl p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <Info className="h-5 w-5 text-blue-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                              About Referrals
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <ul className="space-y-1">
                                <li>• Only verified alumni can make referrals</li>
                                <li>• You'll be notified of new referrals</li>
                                <li>• Track referral status in your dashboard</li>
                                <li>• Set clear expectations for referrers</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    if (currentStep > 0) {
                      setCurrentStep(currentStep - 1);
                    }
                  }}
                  className={`inline-flex items-center px-4 py-2 border border-gray-300 
                    text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50
                    ${currentStep === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={currentStep === 0}
                >
                  Previous
                </button>

                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate("/jobs")}
                    className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>

                  {currentStep < formSteps.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="inline-flex items-center px-6 py-2.5 border border-transparent 
                        text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 
                        hover:bg-blue-700"
                    >
                      Next Step
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className={`inline-flex items-center px-6 py-2.5 border border-transparent 
                        text-sm font-medium rounded-lg shadow-sm text-white
                        ${loading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"}
                        transition-colors duration-200`}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2 h-5 w-5" />
                          Creating Job...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-5 w-5" />
                          Post Job
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="fixed bottom-20 right-4">
            <div className="bg-red-50 text-red-800 p-4 rounded-lg shadow-lg flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div>
                <h3 className="font-medium">Error</h3>
                <p className="text-sm mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError("")}
                className="text-red-600 hover:text-red-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateJob;